import {
  Box,
  Button,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  useTheme,
} from '@mui/material';
import { formatMembershipName } from '@provision/service-level-utils';
import { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { DatePicker } from 'src/components/DatePicker';
import { Address, useOfferedProducts } from 'src/graphql';
import {
  CUSTOM_LABEL,
  DEFAULT_CPP_LENGTH_DAYS,
  getCppLengthList,
  getCppOptionFromList,
  MAX_DATE,
  TODAY_DATE,
  TOMORROW_DATE,
} from './const';
import { CustomChip } from './custom-chip';

export interface AddComplimentaryPeriodFormValues {
  partnerProductId: string;
  cppExpirationDate: Dayjs | null;
}

interface AddComplimentaryPeriodFormProps {
  formMethods: UseFormReturn<AddComplimentaryPeriodFormValues>;
  address?: Partial<Address> | null;
  onSubmit?: (data: AddComplimentaryPeriodFormValues) => void;
  // When used in submit day1, this value is gonna be used as the minimum date for the expiration date
  baseDate?: Dayjs | null;
}

function isDateWithinAllowedBoundariesForCPP(date: Dayjs | null) {
  if (!date) return false;

  return !date.isBefore(TOMORROW_DATE) && !date.isAfter(MAX_DATE);
}

export const AddComplimentaryPeriodForm = ({
  formMethods,
  onSubmit,
  address,
  baseDate,
}: AddComplimentaryPeriodFormProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = formMethods;
  const theme = useTheme();
  const { offeredProducts, loading: isLoadingOfferedProducts } = useOfferedProducts({
    brandId: address?.brandId,
    partnerId: address?.partnerId,
  });
  const sortedOfferedProducts = useMemo(
    () => offeredProducts?.slice().sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [offeredProducts],
  );

  const cppLengthList = useMemo(
    () => getCppLengthList(baseDate?.toDate() || TODAY_DATE),
    [baseDate],
  );

  const DEFAULT_CPP_LENGTH = useMemo(
    () => getCppOptionFromList(cppLengthList, DEFAULT_CPP_LENGTH_DAYS)!,
    [cppLengthList],
  );

  const [selectedChip, setSelectedChip] = useState(cppLengthList[0]);

  useEffect(() => {
    if (sortedOfferedProducts?.length) {
      setValue('partnerProductId', sortedOfferedProducts[0].partnerProductId);
    }
  }, [sortedOfferedProducts, setValue, cppLengthList]);

  useEffect(() => {
    if (baseDate) {
      setSelectedChip(DEFAULT_CPP_LENGTH);
      setValue('cppExpirationDate', DEFAULT_CPP_LENGTH.value);
    }
  }, [DEFAULT_CPP_LENGTH, baseDate, cppLengthList, setValue]);

  const onChipChange = useCallback(
    (item: (typeof cppLengthList)[number]) => {
      setSelectedChip(item);
      setValue('cppExpirationDate', item.value);
    },
    [setValue],
  );

  const onDateChange = useCallback(
    (value: Dayjs | null) => {
      if (!value) return;

      setSelectedChip({ label: CUSTOM_LABEL, value, daysAmount: 1 });
      setValue('cppExpirationDate', value);
    },
    [setValue],
  );

  return (
    <form onSubmit={onSubmit ? handleSubmit(onSubmit) : undefined}>
      <FormControl fullWidth margin="normal">
        <InputLabel id="serviceLevel-label">Service Level</InputLabel>
        <Controller
          name="partnerProductId"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              {...field}
              labelId="serviceLevel-label"
              label="Service Level"
              size="small"
              disabled={isLoadingOfferedProducts}
              value={field.value || ''}
            >
              {sortedOfferedProducts?.map((product) => (
                <MenuItem key={product.partnerProductId} value={product.partnerProductId}>
                  {formatMembershipName(product.displayName)}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </FormControl>

      <Box sx={{ marginTop: theme.spacing(2) }}>
        <Stack direction="row" justifyContent="flex-start" flexWrap="wrap" mb={1}>
          {cppLengthList.map((el) => (
            <CustomChip
              key={el.label}
              label={el.label}
              checked={el.label === selectedChip.label}
              onClick={() => onChipChange(el)}
            />
          ))}
        </Stack>

        <FormControl fullWidth sx={{ marginTop: 1 }}>
          <Controller
            name="cppExpirationDate"
            control={control}
            rules={{
              required: 'Expiration date is required',
              validate: (value) =>
                isDateWithinAllowedBoundariesForCPP(value) ||
                'Expiration date must be between tomorrow and 2 years from now',
            }}
            render={({ field }) => {
              return (
                <DatePicker
                  label="CPP Expiration Date"
                  value={field.value}
                  datePickerProps={{
                    disablePast: true,
                    minDate: TOMORROW_DATE,
                    maxDate: MAX_DATE,
                    disabled: selectedChip.label !== CUSTOM_LABEL,
                  }}
                  onChange={(date) => {
                    field.onChange(date);
                    onDateChange(date);
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: !!errors.cppExpirationDate,
                      helperText: errors.cppExpirationDate?.message || '',
                    },
                  }}
                />
              );
            }}
          />
        </FormControl>
      </Box>

      {onSubmit ? (
        <DialogActions sx={{ marginTop: '1rem' }}>
          <Button disabled={isSubmitting} variant="text" color="primary" type="submit">
            Submit
          </Button>
        </DialogActions>
      ) : null}
    </form>
  );
};
